import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  boxItemsForSize,
  boxIngredients,
  BOX_SIZES,
  type BoxSize,
  type PakketResponse,
  type PakketSection,
} from '@familyhub/shared';
import { api, isOffline } from '../api/client';
import { useRefreshOnReturn } from '../useRefreshOnReturn';
import { Skeleton } from '../components/Loading';

/**
 * The weekly fruit/veg box.
 *
 * Online-only, like everything that asks a live question (weather, compare) — but
 * it must never look broken, so the last good answer is held in memory across
 * re-mounts, a fresh fetch replaces it, and a failure with nothing cached shows
 * one quiet line. It refreshes on the same triggers the sync loop uses.
 *
 * A box lists one or more box types; within each, the size tiers are cumulative.
 * The household gets a Middel box, so the size toggle defaults there — the other
 * tiers are one tap away, and each section notes what a bigger box would add.
 */

/** Survives re-mounts within a session. */
let cached: PakketResponse | null = null;
/** The chosen size sticks across visits — the household's box size rarely changes. */
let cachedSize: BoxSize = 'middel';

const SIZE_LABEL: Record<BoxSize, string> = { klein: 'Klein', middel: 'Middel', groot: 'Groot' };

/** "geplaatst 28 augustus", from WordPress's offset-less local stamp. Empty if unparseable. */
function publishedLabel(publishedAt: string): string {
  const at = new Date(publishedAt);
  if (Number.isNaN(at.getTime())) return '';
  return `geplaatst ${new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' }).format(at)}`;
}

/** The tiers larger than the chosen size that add something — shown as a muted footnote. */
function higherTiers(section: PakketSection, size: BoxSize): { size: BoxSize; items: string[] }[] {
  const out: { size: BoxSize; items: string[] }[] = [];
  if (size === 'klein' && section.middel.length > 0) out.push({ size: 'middel', items: section.middel });
  if (size !== 'groot' && section.groot.length > 0) out.push({ size: 'groot', items: section.groot });
  return out;
}

export function PakketScreen() {
  const navigate = useNavigate();
  const [data, setData] = useState<PakketResponse | null>(cached);
  const [size, setSize] = useState<BoxSize>(cachedSize);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api<PakketResponse>('/api/v1/pakket');
      cached = res;
      setData(res);
      setProblem(null);
    } catch (err) {
      // Keep showing the last answer if we have one; only admit a problem with nothing to show.
      if (!cached) setProblem(isOffline(err) ? 'Voor het pakket is verbinding nodig.' : 'Kon het pakket niet laden.');
    }
  }, []);

  useRefreshOnReturn(refresh);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  function chooseSize(next: BoxSize) {
    cachedSize = next;
    setSize(next);
  }

  const hasTiers = !!data && data.sections.some((s) => s.middel.length > 0 || s.groot.length > 0);

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Terug naar het menu">
          ‹ Menu
        </Link>
        <h1>Pakket</h1>
      </div>

      <main className="stack">
        {problem && <p className="banner">{problem}</p>}

        {!data && !problem && <PakketSkeleton />}

        {data && (
          <>
            <p className="pakket-meta">
              {data.week != null && <span className="pakket-week">Week {data.week}</span>}
              {publishedLabel(data.published_at) && (
                <span className="pakket-published"> · {publishedLabel(data.published_at)}</span>
              )}
            </p>

            {/* The feature the box was built to feed: recipes from this week's
                produce (RECEPTEN.md §6). Hands the box to the recepten screen as
                nav state — a URL seam, no cross-module import. */}
            <button
              type="button"
              className="btn btn-primary btn-lg pakket-generate"
              onClick={() =>
                navigate('/recepten/genereren', {
                  state: { ingredients: boxIngredients(data, size), size, week: data.week },
                })
              }
            >
              Recepten uit dit pakket
            </button>

            {hasTiers && (
              <div className="chips" role="group" aria-label="Maat van het pakket">
                {BOX_SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="chip"
                    aria-pressed={size === s}
                    onClick={() => chooseSize(s)}
                  >
                    {SIZE_LABEL[s]}
                  </button>
                ))}
              </div>
            )}

            {data.sections.map((section, i) => {
              const items = boxItemsForSize(section, size);
              const more = higherTiers(section, size);
              return (
                <article className="card pakket-section" key={`${section.label ?? 'deze-week'}-${i}`}>
                  <h2 className="pakket-section-title">{section.label ?? 'Deze week'}</h2>
                  {items.length > 0 ? (
                    <ul className="pakket-items">
                      {items.map((item, j) => (
                        <li className="pakket-item" key={`${item}-${j}`}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    // A paragraph that didn't split into items still shows its text.
                    <p className="pakket-raw">{section.raw}</p>
                  )}
                  {more.length > 0 && (
                    <p className="pakket-more">
                      {more.map((tier, k) => (
                        <span key={tier.size}>
                          {k > 0 && ' · '}
                          <b>{SIZE_LABEL[tier.size]}</b> ook {tier.items.join(', ')}
                        </span>
                      ))}
                    </p>
                  )}
                </article>
              );
            })}

            {data.sections.length === 0 && !problem && (
              <p className="empty">Nog geen pakketinhoud gevonden.</p>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/** The box, arriving: a meta line, the generate button, and two produce sections
 *  as rows of chip skeletons — the real layout, so nothing jumps when it lands. */
const SK_CHIP_ROWS = [
  [64, 92, 52, 76, 60, 84],
  [80, 56, 72, 64, 96],
];

function PakketSkeleton() {
  return (
    <div className="pakket-skeleton" role="status" aria-label="Pakket laden…">
      <Skeleton w={150} h={13} />
      <Skeleton className="sk-generate" h={54} />
      {SK_CHIP_ROWS.map((widths, s) => (
        <div className="card pakket-section" key={s}>
          <Skeleton w={120} h={16} style={{ marginBottom: 12 }} />
          <div className="sk-chip-row">
            {widths.map((w, j) => (
              <Skeleton key={j} className="sk-chip" w={w} h={30} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
